import React from "react";
import { useParams } from "react-router-dom";

import { useOfferingDailyAccessQuantityFieldFeatureFlag } from "../core/splitio";
import { getMaxCapacity } from "../data";
import { useGetPartnerOffering } from "../data/api";
import { useI18n } from "../lib/i18n/i18n_provider";
import { getFieldFromObject } from "../offering_v2/utils/get_field_from_object";
import {
  Order,
  OrderableType,
  OrderItem,
  OrderItemDayPass,
  useLocationV2Query,
  useOrderQuery,
  useSpaceV2Query,
} from "./api";
import { getMainOrderItem } from "./order";
import { PartnerConfig } from "./partner_config";
import { TemplateOfferingConfirmOrderBase } from "./template_offering_confirm_order_base";
import {
  DayPassDesktopCheckoutBoxOrderPreview,
  DayPassMobileCheckoutBoxOrderPreview,
} from "./template_offering_confirm_order_daypass";
import {
  MultiPassDesktopCheckoutBoxOrderPreview,
  MultiPassMobileCheckoutBoxOrderPreview,
} from "./template_offering_confirm_order_multipass";

interface TemplateOfferingConfirmOrderDailyAccessParams {
  orderID: string;
  offeringID: string;
  spaceID: string;
}

interface TemplateOfferingConfirmOrderDailyAccessProps {
  showTalkWithUsPrompt?: boolean;
  partnerConfig: PartnerConfig;
  confirmationMessage?: (
    order: Order,
    orderItem: OrderItem | undefined
  ) => React.ReactNode;
}

export function TemplateOfferingConfirmOrderDailyAccess(
  props: TemplateOfferingConfirmOrderDailyAccessProps
) {
  const { partnerConfig, showTalkWithUsPrompt, confirmationMessage } = props;
  const pageParams = useParams<TemplateOfferingConfirmOrderDailyAccessParams>();
  const order = useOrderQuery(pageParams.orderID);
  const orderItem = getMainOrderItem(order);
  const { fbt } = useI18n();
  if (!orderItem) {
    throw new Error("Invalid order");
  }
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const spaceID = getFieldFromObject("details", orderItem)?.spaceID;
  const space = useSpaceV2Query(spaceID);
  const location = useLocationV2Query(space.locationID);
  const displayQuantityField = useOfferingDailyAccessQuantityFieldFeatureFlag();
  const includeQuantity = partnerConfig.includeQuantity || false;

  return (
    <TemplateOfferingConfirmOrderBase
      numberOfInvites={
        getMaxCapacity(space) > 1
          ? getMaxCapacity(space)
          : displayQuantityField && orderItem.quantity > 1
          ? orderItem.quantity
          : undefined
      }
      partnerOfferingID={partnerOfferingID}
      partnerOffering={partnerOffering}
      offeringName={
        space?.productType && location
          ? fbt(
              fbt.param("productTypeName", space.productType?.name) +
                " to " +
                fbt.param("locationName", location.name)
            )
          : ""
      }
      partnerConfig={partnerConfig}
      confirmationMessage={confirmationMessage}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      order={order}
      location={location}
      space={space}
      mobileCheckoutBox={
        orderItem.type === OrderableType.Booking ||
        orderItem.type === OrderableType.Membership ? (
          <DayPassMobileCheckoutBoxOrderPreview
            location={location}
            includeQuantity={includeQuantity}
            orderItem={orderItem as OrderItemDayPass}
            quantityLabel={displayQuantityField ? "Attendees:" : undefined}
            quantityIcon="capacity"
          />
        ) : (
          <MultiPassMobileCheckoutBoxOrderPreview
            location={location}
            orderItem={orderItem}
          />
        )
      }
      desktopCheckoutBox={
        orderItem.type === OrderableType.Booking ||
        orderItem.type === OrderableType.Membership ? (
          <DayPassDesktopCheckoutBoxOrderPreview
            location={location}
            includeQuantity={includeQuantity}
            orderItem={orderItem as OrderItemDayPass}
            quantityLabel={displayQuantityField ? "Attendees:" : undefined}
            quantityIcon="capacity"
          />
        ) : (
          <MultiPassDesktopCheckoutBoxOrderPreview
            location={location}
            orderItem={orderItem}
          />
        )
      }
    />
  );
}
